import { Pill, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

interface MatchedNutrient {
  nutrient: string;
  name_en: string | null;
  category: string | null;
  somatic_symptoms: string[] | null;
  consciousness_symptoms: string[] | null;
  vitality_symptoms: string[] | null;
  amway_product_url: string | null;
}

interface Props {
  nutrients: MatchedNutrient[];
}

export default function NutrientPanel({ nutrients }: Props) {
  if (!nutrients || nutrients.length === 0) return null;

  return (
    <div className="space-y-3 md:space-y-4">
      <h3 className="text-base md:text-lg font-semibold text-foreground flex items-center gap-2">
        <Pill className="h-4 w-4 md:h-5 md:w-5 text-primary" />
        Nutrient Recommendations
      </h3>
      <p className="text-xs md:text-sm text-muted-foreground">
        Based on detected somatic symptoms, these nutrients may support your well-being
      </p>

      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
        {nutrients.map((n, i) => (
          <div
            key={i}
            className="rounded-lg border border-border bg-card p-3 md:p-4 hover:shadow-sm transition-shadow space-y-2"
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <h4 className="font-semibold text-foreground text-sm md:text-base">{n.nutrient}</h4>
                {n.name_en && n.name_en !== n.nutrient && (
                  <p className="text-[10px] md:text-xs text-muted-foreground">{n.name_en}</p>
                )}
              </div>
              {n.category && (
                <span className="text-[10px] px-1.5 md:px-2 py-0.5 rounded-full bg-muted text-muted-foreground shrink-0">
                  {n.category}
                </span>
              )}
            </div>

            {/* Symptom tags */}
            {n.somatic_symptoms && n.somatic_symptoms.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {n.somatic_symptoms.slice(0, 4).map((s, j) => (
                  <span key={j} className="text-[10px] px-1.5 py-0.5 rounded bg-destructive/10 text-destructive">
                    {s}
                  </span>
                ))}
              </div>
            )}

            {n.amway_product_url && (
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-1 text-xs"
                asChild
              >
                <a href={n.amway_product_url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-3 w-3 mr-1.5" />
                  View Recommended Product
                </a>
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
